import { BrandsPage, WishlistPage } from './pages/BrandsPage.js';
import { NotFound, RouteErrorBoundary } from '@arabat/web-core';
import type { RouteObject } from 'react-router';
import {
  AccountAddressesPage,
  AccountLayout,
  AccountOrdersPage,
  AccountProfilePage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from './pages/AccountPages.js';
import { CartPage } from './pages/CartPage.js';
import { CheckoutPage } from './pages/CheckoutPage.js';
import { DesignedPage } from './design/sections.js';
import { GroupPage, ListPage } from './pages/GroupPage.js';
import { HomePage } from './pages/HomePage.js';
import { InvoicePage } from './pages/InvoicePage.js';
import { LinksPage } from './pages/LinksPage.js';
import { OrderPage } from './pages/OrderPage.js';
import { PayPage } from './pages/PayPage.js';
import { ProductPage } from './pages/ProductPage.js';
import { StoreLayout } from './pages/StoreLayout.js';

/** Route tree of /store (relative to the /store basename). See docs/ROUTES.md. */
export const routes: RouteObject[] = [
  { path: '/', element: <HomePage />, errorElement: <RouteErrorBoundary /> },
  // Sandbox gateway page (mock adapter only; 'pay' is a reserved store address).
  { path: '/pay/:reference', element: <PayPage />, errorElement: <RouteErrorBoundary /> },
  // Link page of a merchant (ADR-0116; 'links' is a reserved store address).
  { path: '/links/:slug', element: <LinksPage />, errorElement: <RouteErrorBoundary /> },
  // An order's invoice: a page of its own, outside the store's header and footer (ADR-0138).
  {
    path: '/:slug/orders/:token/invoice',
    element: <InvoicePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/:slug',
    element: <StoreLayout />,
    errorElement: <RouteErrorBoundary />,
    children: storePages(),
  },
];

/** On the store's own sub-domain (ADR-0136) its pages are at the root. */
export const hostRoutes: RouteObject[] = [
  {
    path: '/orders/:token/invoice',
    element: <InvoicePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: <StoreLayout />,
    errorElement: <RouteErrorBoundary />,
    children: storePages(),
  },
];

/** The pages of one store, relative to its address. */
function storePages(): RouteObject[] {
  return [
    // Pages built from the store design's sections (ADR-0128).
    { index: true, element: <DesignedPage page="home" /> },
    { path: 'products', element: <ListPage page="products" /> },
    { path: 'categories', element: <ListPage page="categories" /> },
    // Readable addresses (migration 0107): a handle, or an id / former handle that moves to it.
    { path: 'products/:handle', element: <ProductPage /> },
    { path: 'categories/:handle', element: <GroupPage kind="category" /> },
    { path: 'collections/:handle', element: <GroupPage kind="collection" /> },
    { path: 'brands/:handle', element: <GroupPage kind="brand" /> },
    { path: 'brands', element: <BrandsPage /> },
    { path: 'wishlist', element: <WishlistPage /> },
    { path: 'cart', element: <CartPage /> },
    { path: 'checkout', element: <CheckoutPage /> },
    { path: 'orders/:token', element: <OrderPage /> },
    { path: 'account/forgot', element: <ForgotPasswordPage /> },
    { path: 'account/reset/:token', element: <ResetPasswordPage /> },
    {
      path: 'account',
      element: <AccountLayout />,
      children: [
        { index: true, element: <AccountOrdersPage /> },
        { path: 'addresses', element: <AccountAddressesPage /> },
        { path: 'profile', element: <AccountProfilePage /> },
      ],
    },
    { path: '*', element: <NotFound /> },
  ];
}
